import { BaseEntity } from '../base-entity';
import { OrganisationPrincipal } from './organisation-principal';
import { PbiOrganisationWorkspace } from './pbi-organisation-workspace';
import { TemplateWorkspace } from './template-workspace';

/// <code-import> Place custom imports between <code-import> tags
import { EntityName } from '@mri-platform/dsg/core';
import { AuthzContext, AuthzContextsActionMap, CrudAction } from '@mri-platform/resource-authz';
import { NEW_BREEZE_GUID } from '@mri/breeze-entity';
import { DataProperty, DataType, MetadataStore } from 'breeze-client';
import { OrganisationWorkspaceInfo, WorkspaceIdentifiers, WorkspaceType, ActiveDeploymentStatus } from './organisation-workspace-info';
import { validModuleCodeRegEx } from './shared-validators';
/// </code-import>

export interface OrganisationWorkspace {
  id: string;
  capacityId?: string;
  deployStateUpdatedOn: Date;
  enforceAnyStandardRlsRoles: boolean;
  isDeleted: boolean;
  lastUpdatedBy: string;
  lastUpdatedDate: Date;
  moduleCodes?: string;
  ownerId: string;
  periodEnd: Date;
  periodStart: Date;
  region?: string;
  servicePrincipalProfile: string;
  templateWorkspaceId: string;
  tenantKey: string;
  workspaceQualifier: string;
  owner: OrganisationPrincipal;
  pbiWorkspaces: PbiOrganisationWorkspace[];
  templateWorkspace: TemplateWorkspace;
}

/// <module-code> Place module level code between <module-code> tags
export interface OrganisationWorkspace extends OrganisationWorkspaceInfo, WorkspaceIdentifiers {
  activeDeploymentStatus: ActiveDeploymentStatus | null;
}

const typeFuncs = BaseEntity.makeStaticTypeFunctions<OrganisationWorkspace>(EntityName.OrganisationWorkspace);
/// </module-code>

export class OrganisationWorkspace extends BaseEntity {
  /// <code> Place custom code between <code> tags
  static propName = typeFuncs.propName;
  static propNames = typeFuncs.propNames;
  static qualifiedPropName = typeFuncs.qualifiedPropName;
  static qualifiedPropNames = typeFuncs.qualifiedPropNames;

  get modules() {
    return TemplateWorkspace.getModules(this.moduleCodes);
  }

  get workspaceQualifiedLongLabel(): string {
    if (!this.entityAspect.isNavigationPropertyLoaded(OrganisationWorkspace.propName('templateWorkspace'))) {
      return '<<not loaded>>';
    }
    return `${this.templateWorkspace.longLabel}${
      this.workspaceQualifier === WorkspaceType.Primary ? '' : ` (${this.workspaceQualifier})`
    }`;
  }

  get workspaceQualifiedShortLabel(): string {
    if (!this.entityAspect.isNavigationPropertyLoaded(OrganisationWorkspace.propName('templateWorkspace'))) {
      return '<<not loaded>>';
    }
    return `${this.templateWorkspace.shortLabel}${
      this.workspaceQualifier === WorkspaceType.Primary ? '' : ` (${this.workspaceQualifier})`
    }`;
  }

  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  set modules(value: string[]) {
    const moduleCodes = value.join(',');
    const allModule =
      this.templateWorkspace.modules.length === value.length || moduleCodes === TemplateWorkspace.modulePlaceHolder;
    this.moduleCodes = allModule || value.length === 0 ? undefined : moduleCodes;
  }
  //Set ServicePrincipalProfile to placeholder value in-order to trigger a client-onboarding on the server-side
  static servicePrincipalPlaceHolder = '71E9456D-B5DC-4C8A-8BF0-626E77F4C934';

  static authorization: AuthzContextsActionMap = {
    ...AuthzContextsActionMap.crudFor(EntityName.OrganisationWorkspace),
    [CrudAction.Create]: AuthzContext.createFor(
      EntityName.OrganisationWorkspace,
      EntityName.PbiDeployment,
      EntityName.OrganisationWorkspaceDeployment
    ),
    [CrudAction.Update]: [
      AuthzContext.updateFor(...OrganisationWorkspace.qualifiedPropNames('moduleCodes', 'capacityId')),
      AuthzContext.createFor(EntityName.PbiDeployment, EntityName.OrganisationWorkspaceDeployment)
    ],
    [CrudAction.Delete]: AuthzContext.updateFor(OrganisationWorkspace.qualifiedPropName('isDeleted'))
  };

  static register(metadataStore: MetadataStore) {
    const et = metadataStore.getAsEntityType(EntityName.OrganisationWorkspace, false);
    const prop = et.getProperty('moduleCodes');
    prop.validators = [...prop.validators, validModuleCodeRegEx];

    et.addProperty(
      new DataProperty({
        name: OrganisationWorkspace.propName('activeDeploymentStatus'),
        dataType: DataType.String,
        isNullable: true,
        isPartOfKey: false,
        defaultValue: null,
        validators: []
      })
    );
  }

  override setDeleted() {
    if (!this.entityAspect.entityState.isDeleted() && this.templateWorkspaceId !== NEW_BREEZE_GUID) this.modules = [];
    return super.setDeleted();
  }
  /// </code>
  // Generated code. Do not place code below this line.
}
